import { useState, useEffect, useCallback } from 'react';
import { Settings as SettingsIcon, Plus, Edit, Trash2, FolderOpen, Tag, Save, X } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Toast from '../components/common/Toast';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorMessage from '../components/common/ErrorMessage';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../services/categoryService';
import './Settings.css';

export default function Settings() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', team: '' });

  // New category form
  const [showAddForm, setShowAddForm] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', description: '', team: 'UK & I Reporting' });
  const [saving, setSaving] = useState(false);

  const fetchCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCategories();
      setCategories(data);
    } catch (err) {
      setError(err.message || 'Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const showToast = (message, type = 'success') => setToast({ message, type });

  // Add category
  const handleAdd = async () => {
    if (!addForm.name.trim()) { showToast('Category name is required', 'error'); return; }
    try {
      setSaving(true);
      await createCategory(addForm);
      showToast('Category created');
      setAddForm({ name: '', description: '', team: 'UK & I Reporting' });
      setShowAddForm(false);
      fetchCategories();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to create category', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Start editing
  const startEdit = (cat) => {
    setEditingId(cat.id);
    setEditForm({ name: cat.name, description: cat.description, team: cat.team });
  };

  // Save edit
  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) { showToast('Category name is required', 'error'); return; }
    try {
      setSaving(true);
      await updateCategory(editingId, editForm);
      showToast('Category updated');
      setEditingId(null);
      fetchCategories();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to update category', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete category
  const handleDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Reports using this category won't be affected.`)) return;
    try {
      await deleteCategory(cat.id);
      showToast('Category deleted');
      fetchCategories();
    } catch (err) {
      showToast(err.response?.data?.message || err.message || 'Failed to delete category', 'error');
    }
  };

  return (
    <div className="settings-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-text">
            <h1 className="page-title"><SettingsIcon size={32} /> Settings</h1>
            <p className="page-subtitle">Manage application configuration</p>
          </div>
        </div>
      </div>

      {/* Categories Section */}
      <Card className="settings-card">
        <div className="settings-section-header">
          <div className="section-title">
            <FolderOpen size={22} />
            <h2>Report Categories</h2>
          </div>
          <Button variant="primary" onClick={() => setShowAddForm(true)} icon={<Plus size={18} />} disabled={showAddForm}>
            Add Category
          </Button>
        </div>

        {/* Add form */}
        {showAddForm && (
          <div className="category-form">
            <div className="form-row-inline">
              <input
                type="text" placeholder="Category name" value={addForm.name}
                onChange={e => setAddForm(prev => ({ ...prev, name: e.target.value }))}
                className="form-input" autoFocus
              />
              <input
                type="text" placeholder="Description (optional)" value={addForm.description}
                onChange={e => setAddForm(prev => ({ ...prev, description: e.target.value }))}
                className="form-input"
              />
              <input
                type="text" placeholder="Team / Domain" value={addForm.team}
                onChange={e => setAddForm(prev => ({ ...prev, team: e.target.value }))}
                className="form-input form-input-team"
              />
              <div className="form-actions-inline">
                <button className="btn-icon btn-save" onClick={handleAdd} disabled={saving} title="Save">
                  <Save size={18} />
                </button>
                <button className="btn-icon btn-cancel" onClick={() => { setShowAddForm(false); setAddForm({ name: '', description: '', team: 'UK & I Reporting' }); }} title="Cancel">
                  <X size={18} />
                </button>
              </div>
            </div>
          </div>
        )}

        {loading && <LoadingSpinner />}
        {error && <ErrorMessage message={error} onRetry={fetchCategories} />}

        {!loading && !error && (
          <div className="categories-list">
            {categories.length === 0 ? (
              <p className="empty-state">No categories yet. Add one to get started.</p>
            ) : (
              <table className="categories-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Team / Domain</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {categories.map(cat => (
                    <tr key={cat.id}>
                      {editingId === cat.id ? (
                        <>
                          <td>
                            <input type="text" value={editForm.name}
                              onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                              className="form-input" />
                          </td>
                          <td>
                            <input type="text" value={editForm.description}
                              onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                              className="form-input" />
                          </td>
                          <td>
                            <input type="text" value={editForm.team}
                              onChange={e => setEditForm(prev => ({ ...prev, team: e.target.value }))}
                              className="form-input" />
                          </td>
                          <td>
                            <div className="action-buttons">
                              <button className="action-btn save-btn" onClick={handleSaveEdit} disabled={saving} title="Save"><Save size={16} /></button>
                              <button className="action-btn cancel-btn" onClick={() => setEditingId(null)} title="Cancel"><X size={16} /></button>
                            </div>
                          </td>
                        </>
                      ) : (
                        <>
                          <td><span className="category-name"><FolderOpen size={16} /> {cat.name}</span></td>
                          <td className="description-cell">{cat.description || '—'}</td>
                          <td>{cat.team ? <span className="team-badge"><Tag size={14} /> {cat.team}</span> : '—'}</td>
                          <td>
                            <div className="action-buttons">
                              <button className="action-btn edit-btn" onClick={() => startEdit(cat)} title="Edit"><Edit size={16} /></button>
                              <button className="action-btn delete-btn" onClick={() => handleDelete(cat)} title="Delete"><Trash2 size={16} /></button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </Card>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} duration={5000} />}
    </div>
  );
}
